import { TinkerBoxType } from '@convergence/client';
import { Table } from '@convergence/components';
import { Pause, Play } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';
import { ComponentProps, useEffect } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import { useStartTinkerBoxMutation } from '../api/mutations/startTinkerBox';
import { useStopTinkerBoxMutation } from '../api/mutations/stopTinkerBox';
import { ConnectionState } from '../stores/TinkerBoxConnectionStore';
import { ConnectButton } from './ConnectButton';
import { StatusIndicator } from './StatusIndicator';

type TinkerBoxStatus = TinkerBoxType['status'];

export interface TinkerBoxTableRow {
  id: number;
  lastLogin?: unknown;
  name: string;
  status: TinkerBoxStatus;
  ipAddress?: string | null | undefined;
}

interface TinkerBoxTableProps {
  tinkerBoxes: TinkerBoxType[];
  className?: string;
  onConnect: (tinkerBoxId: number | null) => void;
  onStop?: (tinkerBoxId: number) => void;
  selectedTinkerBoxId?: number | null | undefined;
  connectionState?: ConnectionState;
  hideIpAddress?: boolean;
  onStart?: (tinkerBoxId: number) => void;
}

const Cell = (props: ComponentProps<'td'>) => {
  return (
    <td
      {...props}
      className="border border-black min-w-9 p-1 aspect-square whitespace-nowrap break-words text-ellipsis text-xs last:w-full"
    >
      <div className="grid place-items-center text-center">
        {props.children}
      </div>
    </td>
  );
};

const RowStyles = cva(['max-w-full border border-black', 'w-full'], {
  variants: {
    connected: {
      true: ['bg-green-200'],
      false: []
    },
    connecting: {
      true: ['flashing-green'],
      false: []
    },

    disconnected: {
      true: ['bg-white'],
      false: []
    }
  }
});

const Row = (
  props: ComponentProps<'tr'> & {
    connecting: boolean;
    connected: boolean;
    disconnected: boolean;
  }
) => {
  const {
    className = '',
    connected,
    connecting,
    disconnected,
    ...rest
  } = props;
  return (
    <tr
      className={twMerge(
        RowStyles({ connected, connecting, disconnected }),
        className
      )}
      {...rest}
    >
      {props.children}
    </tr>
  );
};

const StateSymbol = ({ status }: { status: TinkerBoxStatus }) => {
  if (status === 'running' || status === 'pending') {
    return <Pause size={14} />;
  } else return <Play size={14} />;
};

export const TinkerBoxTable = ({
  tinkerBoxes,
  className = '',
  onConnect,
  onStop,
  selectedTinkerBoxId,
  connectionState,
  hideIpAddress = false,
  onStart
}: TinkerBoxTableProps) => {
  const { mutateAsync: startTinkerBox, error: startTinkerBoxError } =
    useStartTinkerBoxMutation();
  const { mutateAsync: stopTinkerBox, error: stopTinkerBoxError } =
    useStopTinkerBoxMutation();

  useEffect(() => {
    const error = startTinkerBoxError || stopTinkerBoxError;
    if (!error) return;
    toast.error(`Error: ${error?.message ?? 'An unknown error occurred'}`);
  }, [startTinkerBoxError, stopTinkerBoxError]);

  return (
    <>
      <Table
        className={`text-neutral-800 w-full ${className} table-fixed overflow-hidden`}
        cell={Cell}
        data={tinkerBoxes.map(({ status, id, name, ipAddress }) => ({
          status,
          playPause: '',
          connect: '',
          id,
          ipAddress,
          name
        }))}
        renderHeading={() => null}
        row={(props, data) => (
          <Row
            {...props}
            connecting={
              data.id === selectedTinkerBoxId &&
              connectionState === 'connecting'
            }
            connected={
              data.id === selectedTinkerBoxId && connectionState === 'connected'
            }
            disconnected={connectionState === 'disconnected'}
            data-testid={`tinkerbox-row`}
          />
        )}
        defaultSortKey="name"
        render={{
          name: ({ name }) => (
            <p
              className="whitespace-nowrap overflow-hidden text-ellipsis w-full block min-w-[]"
              data-tour="name"
            >
              {name}
            </p>
          ),
          status: ({ status }) => (
            <StatusIndicator
              statuses={{
                pending: {
                  color: '#ffc107',
                  pulse: true
                },
                stopping: {
                  color: '#ffc107',
                  pulse: true
                },
                shutting_down: {
                  color: '#ffc107',
                  pulse: true
                },
                running: {
                  color: '#28a745'
                },
                stopped: {
                  color: '#dc3545'
                },
                terminated: {
                  color: '#dc3545'
                }
              }}
              data-tour="status"
              status={status as TinkerBoxStatus}
            />
          ),
          ipAddress: ({ ipAddress }) => ipAddress || '<none>',
          playPause: ({ status, id }) => (
            <button
              data-testid={`tinkerbox-play-pause`}
              data-tour="play-pause"
              className="disabled:text-neutral-500 disabled:cursor-default cursor-pointer hover:scale-110 transition-transform"
              onClick={async () => {
                if (status === 'running') {
                  await stopTinkerBox(id);
                  toast.success('TinkerBox Stop Request Sent');
                  onStop?.(id);
                } else if (status === 'stopped') {
                  await startTinkerBox(id);
                  onStart?.(id);
                  toast.success('TinkerBox Start Request Sent');
                }
              }}
            >
              <StateSymbol status={status as TinkerBoxStatus} />
            </button>
          ),
          connect: ({ status, id }) => (
            <ConnectButton
              data-testid={`tinkerbox-connect`}
              data-tour="connect"
              connected={
                id === selectedTinkerBoxId && connectionState === 'connected'
              }
              onClick={async () => {
                if (status === 'pending') {
                  toast.info(
                    'TinkerBox is still starting up...Connecting shortly'
                  );
                } else if (status === 'stopped') {
                  await startTinkerBox(id);
                  toast.success(
                    'TinkerBox Start Request Sent...Connecting shortly'
                  );
                }
                connectionState === 'connected'
                  ? onConnect(null)
                  : onConnect(id);
              }}
            />
          )
        }}
        hide={{ id: true, ipAddress: hideIpAddress }}
      />
    </>
  );
};

// className="hover:scale-110 transition-transform"
//          data-testid={`tinkerbox-connect`}
//          data-tour="connect"
//          onClick={() => onConnect(id)}
//          disabled={status !== 'running'}
//      >
//          {id === selectedTinkerBoxId && connectionState === 'connected' ? (
//              <PlugsConnected size={20} />
//          ) : (
//              <Plugs size={20} />
//          )}
//      </button>
